import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Search, UploadCloud, Users } from 'lucide-react';
import { toast } from 'sonner';

const BASE = process.env.REACT_APP_BACKEND_URL;

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '');

function ChapterCounts({ items }) {
  const counts = items.reduce((acc, m) => ({ ...acc, [m.chapter || 'No chapter']: (acc[m.chapter || 'No chapter'] || 0) + 1 }), {});
  return (
    <div className="flex flex-wrap gap-2" data-testid="roster-chapter-counts">
      {Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([c, n]) => (
        <span key={c} className="text-[11px] border border-gold/30 rounded-sm px-2 py-1 text-cbtu-cream">{c} <span className="text-gold font-semibold">{n}</span></span>
      ))}
    </div>
  );
}

export const AdminRoster = ({ password }) => {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [q, setQ] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const headers = { 'X-Admin-Password': password };

  const load = () => axios.get(`${BASE}/api/admin/roster`, { headers }).then((r) => { setItems(r.data.items || []); setMeta(r.data.meta); }).catch(() => toast.error('Could not load roster.'));
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (items.length && !window.confirm(`Replace the current roster (${items.length} members) with "${file.name}"?`)) { fileRef.current.value = ''; return; }
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      const r = await axios.post(`${BASE}/api/admin/roster/upload`, fd, { headers });
      toast.success(`Roster updated: ${r.data.meta.count} members.`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploading(false);
      fileRef.current.value = '';
    }
  };

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return items;
    return items.filter((m) => [m.first_name, m.last_name, `${m.first_name} ${m.last_name}`, m.chapter, m.union, m.local, m.email, m.membership].some((v) => (v || '').toLowerCase().includes(t)));
  }, [items, q]);

  return (
    <div className="space-y-4">
      <div className="card-dark rounded-md p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2"><Users className="text-gold" size={18} /><h3 className="font-heading tracking-widest text-gold text-sm">NATIONAL ROSTER: DUES-PAYING MEMBERS</h3></div>
          <div className="text-xs text-cbtu-muted mt-1" data-testid="roster-meta">
            {meta ? <>{meta.count} members · from <span className="text-cbtu-cream">{meta.filename}</span> · uploaded {fmt(meta.uploaded_at)}</> : 'No roster uploaded yet.'}
          </div>
        </div>
        <label className={`bg-[#2ca01c] hover:bg-[#248a16] text-white font-heading text-xs tracking-widest px-4 py-3 rounded-sm inline-flex items-center gap-2 cursor-pointer transition-colors ${uploading ? 'opacity-60 pointer-events-none' : ''}`} data-testid="roster-upload-btn">
          <UploadCloud size={14} /> {uploading ? 'UPLOADING…' : 'UPLOAD NEW ROSTER (CSV / EXCEL)'}
          <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="roster-file-input" />
        </label>
      </div>

      {items.length > 0 && <ChapterCounts items={items} />}

      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gold" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, chapter, union, local…" className="w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm pl-9 pr-3 py-2 text-sm text-cbtu-cream outline-none" data-testid="roster-search-input" />
      </div>
      <div className="text-xs text-gold font-heading tracking-widest" data-testid="roster-result-count">{filtered.length} OF {items.length} MEMBERS</div>

      <div className="card-dark rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="roster-table">
          <thead className="bg-cbtu-green/40 text-gold font-heading text-[11px] tracking-widest">
            <tr>{['NAME', 'CHAPTER', 'UNION', 'LOCAL', 'MEMBERSHIP', 'EMAIL', 'PHONE'].map((h) => <th key={h} className="text-left px-3 py-3 whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {filtered.map((m) => (
              <tr key={m.id} className="border-b border-gold/10 hover:bg-cbtu-green/10" data-testid={`roster-row-${m.id}`}>
                <td className="px-3 py-2 text-cbtu-cream whitespace-nowrap">{m.first_name} {m.last_name}{m.suffix && !m.last_name.includes(m.suffix) ? ` ${m.suffix}` : ''}</td>
                <td className="px-3 py-2 text-cbtu-cream">{m.chapter || '—'}</td>
                <td className="px-3 py-2 text-cbtu-cream">{m.union || '—'}</td>
                <td className="px-3 py-2 text-cbtu-muted text-xs">{m.local || '—'}</td>
                <td className="px-3 py-2 text-xs text-cbtu-cream">{m.membership || '—'}</td>
                <td className="px-3 py-2 text-xs"><a href={`mailto:${m.email}`} className="text-cbtu-muted hover:text-gold">{m.email}</a></td>
                <td className="px-3 py-2 text-xs text-cbtu-muted whitespace-nowrap">{m.phone}</td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="px-3 py-10 text-center text-cbtu-muted">{items.length ? 'No members match your search.' : 'Upload the national roster to get started.'}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
